// The home's "Needs attention" rows, built from the raw worklist reads (pure,
// unit-tested in __tests__/home-attention.test.ts). D-443 rulings 3 to 5.
//
// A row stands for a GROUP of records that one screen resolves, not for each
// record: eighty rejected floor events with the same reason are one row that
// says eighty and opens the conflicts screen, because a list of eighty
// identical rows is the clutter the owner's review named. A task is the one
// exception. Each task completes with its own press (spec 09 §4.16), so each
// stays a row of its own.
//
// Codes never reach the glass. A conflict's reason goes through
// `production.conflicts.reason.*` and a hand-off's scrap reason through the
// waste pack's own name for it, with the code only as the last fallback.

import type { TFunction } from "i18next";
import type { ChecklistFixRoute } from "../../onboarding/screens/checklist/checklist-catalog";
import type { SalesActivity } from "../sales/hooks/activity-queries";

export type Severity = "bad" | "warn" | "neutral";

export type AttentionBucket = "import" | "setup" | "count" | "floor" | "quality" | "task";

/** How many rows the card shows before "Show all". */
export const ATTENTION_CAP = 8;

/** Where a row's one action goes. A closed union of the screens that resolve
 * a row, so every target is a route the router knows. */
export type AttentionLink =
  | { readonly to: "/onboarding/imports/$id"; readonly params: { readonly id: string } }
  | { readonly to: "/inventory/counts/$id"; readonly params: { readonly id: string } }
  | {
      readonly to:
        | "/inventory/counts"
        | "/production/conflicts"
        | "/production/shifts"
        | "/onboarding/checklist"
        | NonNullable<ChecklistFixRoute>;
    };

export interface AttentionRow {
  readonly key: string;
  readonly severity: Severity;
  readonly bucket: AttentionBucket;
  readonly item: string;
  readonly detail: string;
  /** ISO instant that orders rows of one severity, newest first. */
  readonly at: string;
  readonly actionLabel: string;
  /** Absent on a task row, whose action is Complete rather than a link. */
  readonly link?: AttentionLink;
  /** A task row's own age words ("Overdue 3 d", "Today"). */
  readonly ageText?: string;
  readonly task?: SalesActivity;
}

const SEVERITY_RANK: Record<Severity, number> = { bad: 0, warn: 1, neutral: 2 };

/** Worst first, then newest first. A task's `at` is its due date, so among
 * overdue tasks the most recently due sits highest (spec 09 §4.16). */
export function sortAttention(rows: readonly AttentionRow[]): AttentionRow[] {
  return [...rows].sort((a, b) => {
    const s = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (s !== 0) return s;
    return Date.parse(b.at) - Date.parse(a.at);
  });
}

/** The rows the card shows: all of them once the reader asked, else the cap. */
export function visibleAttention(
  rows: readonly AttentionRow[],
  showAll: boolean,
  cap: number = ATTENTION_CAP,
): readonly AttentionRow[] {
  return showAll ? rows : rows.slice(0, cap);
}

function newestFirst<T>(items: readonly T[], at: (item: T) => string): T[] {
  return [...items].sort((a, b) => Date.parse(at(b)) - Date.parse(at(a)));
}

/** Group `items` by `keyOf`, keeping first-seen order. */
function groupBy<T>(items: readonly T[], keyOf: (item: T) => string): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const item of items) {
    const key = keyOf(item);
    const list = out.get(key);
    if (list === undefined) out.set(key, [item]);
    else list.push(item);
  }
  return out;
}

/* -------------------------------- imports -------------------------------- */

export interface ImportJobLike {
  readonly id: string;
  readonly kind: string;
  readonly file_name: string;
  readonly status: string;
  readonly error_rows: number;
  readonly total_rows: number;
  readonly validated_at: string | null;
  readonly created_at: string;
}

/** Import files with rejected rows the reader can fix. A file whose lane the
 * reader does not hold is not theirs to act on, so it is not their row
 * (D-443 ruling 4). Several files make one row that opens the newest. */
export function importRows(
  t: TFunction,
  jobs: readonly ImportJobLike[],
  canImport: (kind: string) => boolean,
): AttentionRow[] {
  const actionable = newestFirst(
    jobs.filter(
      (job) =>
        job.error_rows > 0 &&
        job.status !== "discarded" &&
        job.status !== "committing" &&
        canImport(job.kind),
    ),
    (job) => job.validated_at ?? job.created_at,
  );
  const newest = actionable[0];
  if (newest === undefined) return [];
  const link: AttentionLink = { to: "/onboarding/imports/$id", params: { id: newest.id } };
  const at = newest.validated_at ?? newest.created_at;
  if (actionable.length === 1) {
    return [
      {
        key: `import-${newest.id}`,
        severity: "bad",
        bucket: "import",
        item: newest.file_name,
        detail: t("home.attention.importDetail", {
          failed: newest.error_rows,
          total: newest.total_rows,
        }),
        at,
        actionLabel: t("home.attention.importAction"),
        link,
      },
    ];
  }
  return [
    {
      key: "import-group",
      severity: "bad",
      bucket: "import",
      item: t("home.attention.importGroup", { count: actionable.length }),
      detail: t("home.attention.newest", { name: newest.file_name }),
      at,
      actionLabel: t("home.attention.importAction"),
      link,
    },
  ];
}

/* ------------------------------- checklist ------------------------------- */

export interface ChecklistItemLike {
  readonly item_key: string;
  readonly status: string;
  readonly blocking: boolean;
  readonly evaluated_at?: string | null;
}

/** Blocking setup checks that still stand. One stays a row that opens its own
 * fix; several make one row that opens the checklist. */
export function checklistRows(
  t: TFunction,
  items: readonly ChecklistItemLike[],
  fixRouteOf: (itemKey: string) => ChecklistFixRoute | undefined,
  fallbackAt: string,
): AttentionRow[] {
  const open = items.filter(
    (item) =>
      item.blocking &&
      (item.status === "failed" || item.status === "pending") &&
      typeof fixRouteOf(item.item_key) === "string",
  );
  const first = open[0];
  if (first === undefined) return [];
  const anyFailed = open.some((item) => item.status === "failed");
  const severity: Severity = anyFailed ? "warn" : "neutral";
  const at = first.evaluated_at ?? fallbackAt;
  const title = (key: string) =>
    t(`onboarding.checklist.items.${key}.title`, { defaultValue: key });
  if (open.length === 1) {
    return [
      {
        key: `checklist-${first.item_key}`,
        severity,
        bucket: "setup",
        item: title(first.item_key),
        detail: t("home.attention.checklistDetail"),
        at,
        actionLabel: t("home.attention.checklistAction"),
        link: { to: fixRouteOf(first.item_key) ?? "/onboarding/checklist" },
      },
    ];
  }
  return [
    {
      key: "checklist-group",
      severity,
      bucket: "setup",
      item: t("home.attention.checklistGroup", { count: open.length }),
      detail: t("home.attention.checklistDetail"),
      at,
      actionLabel: t("home.attention.checklistGroupAction"),
      link: { to: "/onboarding/checklist" },
    },
  ];
}

/* --------------------------------- counts -------------------------------- */

export interface CountLike {
  readonly id: string;
  readonly count_code: string;
  readonly submitted_at: string | null;
  readonly created_at: string;
}

export function countRows(t: TFunction, counts: readonly CountLike[]): AttentionRow[] {
  const sorted = newestFirst(counts, (c) => c.submitted_at ?? c.created_at);
  const newest = sorted[0];
  if (newest === undefined) return [];
  const at = newest.submitted_at ?? newest.created_at;
  if (sorted.length === 1) {
    return [
      {
        key: `count-${newest.id}`,
        severity: "warn",
        bucket: "count",
        item: newest.count_code,
        detail: t("home.attention.countDetail"),
        at,
        actionLabel: t("home.attention.countAction"),
        link: { to: "/inventory/counts/$id", params: { id: newest.id } },
      },
    ];
  }
  return [
    {
      key: "count-group",
      severity: "warn",
      bucket: "count",
      item: t("home.attention.countGroup", { count: sorted.length }),
      detail: t("home.attention.newest", { name: newest.count_code }),
      at,
      actionLabel: t("home.attention.countAction"),
      link: { to: "/inventory/counts" },
    },
  ];
}

/* ------------------------------- conflicts ------------------------------- */

export interface ConflictLike {
  readonly id: string;
  readonly kind: string;
  readonly reason: string;
  readonly created_at: string;
}

/** One row per reason, named in words, with how many events it holds. */
export function conflictRows(t: TFunction, conflicts: readonly ConflictLike[]): AttentionRow[] {
  const out: AttentionRow[] = [];
  for (const [reason, group] of groupBy(conflicts, (c) => c.reason)) {
    const sorted = newestFirst(group, (c) => c.created_at);
    const newest = sorted[0];
    if (newest === undefined) continue;
    out.push({
      key: `conflict-${reason}`,
      severity: "bad",
      bucket: "floor",
      item: t(`production.conflicts.reason.${reason}`, {
        defaultValue: t("home.attention.conflictUnnamed"),
      }),
      detail:
        sorted.length === 1
          ? t(`production.conflicts.kind.${newest.kind}`, {
              defaultValue: t("home.attention.conflictOne"),
            })
          : t("home.attention.conflictGroup", { count: sorted.length }),
      at: newest.created_at,
      actionLabel: t("home.attention.conflictAction"),
      link: { to: "/production/conflicts" },
    });
  }
  return out;
}

/* -------------------------------- hand-offs ------------------------------- */

export interface HandoffLike {
  readonly id: string;
  readonly scrap_reason_code: string;
  readonly qty_defective: string;
  readonly created_at: string;
}

/** One row per scrap reason, named by the pack (`reasonName`), never the code
 * while a name exists. */
export function handoffRows(
  t: TFunction,
  handoffs: readonly HandoffLike[],
  reasonName: (code: string) => string | undefined,
): AttentionRow[] {
  const out: AttentionRow[] = [];
  for (const [code, group] of groupBy(handoffs, (h) => h.scrap_reason_code)) {
    const sorted = newestFirst(group, (h) => h.created_at);
    const newest = sorted[0];
    if (newest === undefined) continue;
    out.push({
      key: `handoff-${code}`,
      severity: "warn",
      bucket: "quality",
      item: reasonName(code) ?? t("home.attention.handoffUnnamed"),
      detail:
        sorted.length === 1
          ? t("home.attention.handoffDetail", { qty: newest.qty_defective })
          : t("home.attention.handoffGroup", { count: sorted.length }),
      at: newest.created_at,
      actionLabel: t("home.attention.handoffAction"),
      link: { to: "/production/shifts" },
    });
  }
  return out;
}
